import type { components } from '@fluencelabs/vodopad-admin-api'
import { getDefaultFilter, useTable } from '@refinedev/antd'
import type { HttpError } from '@refinedev/core'
import { Breadcrumb, Form, Input, Select, Space, Table, Typography } from 'antd'

import { ClusterNameLink } from '@/components/common/ClusterNameLink'
import { StatusTag } from '@/components/common/StatusTag'
import { UserEmailLink } from '@/components/common/UserEmailLink'
import { UserSearchSelect } from '@/components/common/UserSearchSelect'
import { SUBNET_STATUSES } from '@/constants/statuses'
import { useClusters } from '@/hooks/useClusters'
import { useTableRowNavigation } from '@/hooks/useTableRowNavigation'
import { UsersCacheProvider } from '@/hooks/useUsersByIds'
import { getPersistedPageSize, tablePaginationConfig } from '@/types/api'
import { searchByNameOrId } from '@/utils/searchFilters'

type UserSubnetDto = components['schemas']['UserSubnetDto']

export default function SubnetsListPage() {
  const onRow = useTableRowNavigation('subnets')
  const clusters = useClusters()

  const { tableProps, searchFormProps, filters } = useTable<
    UserSubnetDto,
    HttpError,
    { search?: string; status?: string; userId?: string; clusterId?: string }
  >({
    resource: 'subnets',
    pagination: { pageSize: getPersistedPageSize() },
    syncWithLocation: true,
    onSearch: (values) => [
      ...searchByNameOrId(values.search, 'ids', true),
      { field: 'status', operator: 'eq', value: values.status || undefined },
      { field: 'userId', operator: 'eq', value: values.userId || undefined },
      {
        field: 'clusterId',
        operator: 'eq',
        value: values.clusterId || undefined,
      },
    ],
  })

  return (
    <div>
      <Breadcrumb style={{ marginBottom: 16 }} items={[{ title: 'Subnets' }]} />
      <Form
        {...searchFormProps}
        layout="inline"
        style={{ marginBottom: 16 }}
        initialValues={{
          search:
            getDefaultFilter('name', filters, 'eq') ??
            getDefaultFilter('ids', filters, 'eq'),
          status: getDefaultFilter('status', filters, 'eq'),
          clusterId: getDefaultFilter('clusterId', filters, 'eq'),
        }}
      >
        <Space wrap>
          <Form.Item name="search" noStyle>
            <Input.Search
              placeholder="Search by name or ID..."
              style={{ width: 250 }}
              onSearch={() => searchFormProps.form?.submit()}
              allowClear
            />
          </Form.Item>
          <Form.Item name="status" noStyle>
            <Select
              placeholder="Status"
              style={{ width: 140 }}
              allowClear
              onChange={() => searchFormProps.form?.submit()}
              options={SUBNET_STATUSES.map((s) => ({ label: s, value: s }))}
            />
          </Form.Item>
          <Form.Item name="userId" noStyle>
            <UserSearchSelect onChange={() => searchFormProps.form?.submit()} />
          </Form.Item>
          <Form.Item name="clusterId" noStyle>
            <Select
              placeholder="Cluster"
              style={{ width: 200 }}
              allowClear
              showSearch
              optionFilterProp="label"
              onChange={() => searchFormProps.form?.submit()}
              options={clusters.map((c) => ({
                label: c.name ?? c.id,
                value: c.id,
              }))}
            />
          </Form.Item>
        </Space>
      </Form>

      <UsersCacheProvider
        ids={(tableProps.dataSource ?? []).map((row) => row.userId)}
      >
        <Table
          {...tableProps}
          pagination={tablePaginationConfig(tableProps)}
          rowKey="id"
          onRow={onRow}
          columns={[
            { title: 'Name', dataIndex: 'name', key: 'name', ellipsis: true },
            {
              title: 'Status',
              dataIndex: 'status',
              key: 'status',
              width: 100,
              render: (s: string) => <StatusTag status={s} />,
            },
            {
              title: 'IPv4 CIDR',
              dataIndex: 'ipv4Cidr',
              key: 'ipv4Cidr',
              render: (v: string | null) =>
                v ? <Typography.Text copyable>{v}</Typography.Text> : '—',
            },
            {
              title: 'IPv6 CIDR',
              dataIndex: 'ipv6Cidr',
              key: 'ipv6Cidr',
              render: (v: string | null) =>
                v ? <Typography.Text copyable>{v}</Typography.Text> : '—',
            },
            {
              title: 'User',
              dataIndex: 'userId',
              key: 'userId',
              ellipsis: true,
              render: (id: string) =>
                id ? <UserEmailLink userId={id} /> : '—',
            },
            {
              title: 'Cluster',
              dataIndex: 'clusterId',
              key: 'clusterId',
              ellipsis: true,
              render: (id: string) =>
                id ? <ClusterNameLink clusterId={id} /> : '—',
            },
          ]}
          scroll={{ x: 800 }}
        />
      </UsersCacheProvider>
    </div>
  )
}
